/**
 * Describe en palabras qué cambió entre el ANTES y el DESPUÉS de un objeto, para
 * que la bitácora diga algo que se pueda auditar.
 *
 * Por qué existe (F0 del `PLAN-BITACORA-ACTIVITY-LOGS.md`): un renglón de
 * bitácora vale por lo que dice. Hoy `updateCashRegister` arma la frase a mano y
 * solo cuenta el DESPUÉS (*"editó la caja: monto inicial a $500"*), que no sirve
 * para la pregunta que se hace un admin: **de cuánto a cuánto**. Si cada módulo
 * lo resuelve por su cuenta habrá siete formatos distintos de la misma frase.
 *
 * Estilo de la frase: continúa el nombre de quien la hizo, porque el drawer pinta
 * `${name} ${description}` — *"Juan **cambió el precio de $189.00 a $199.00**"*.
 *
 * ⚠️ **Si no cambió nada devuelve `''` — y entonces NO se escribe log.** Guardar
 * un pedido sin tocarlo no es un evento. Es el criterio que evita que "abrir y
 * guardar" ensucie la bitácora y cueste escrituras en la colección más grande del
 * sistema.
 *
 * ⚠️ **Las fechas se comparan por su INSTANTE, no por su forma.** El mismo momento
 * llega como `Timestamp`, como `Date` o como `{seconds, nanoseconds}` según quién
 * escribió el documento, y comparándolos como texto todos son `[object Object]`:
 * **un cambio de fecha pasaría inadvertido**, que es justo el más delicado que hace
 * el admin — mover la fecha de un pedido lo cambia de corte de caja. Se usa
 * `toDateSafe` (`@utils/Date`), la implementación buena; no se escribe otra.
 *
 * Solo cuentan como fecha los OBJETOS (`Date` o Timestamp): un número suelto se
 * queda como número — si no, un precio de 189 se leería como 189 ms desde 1970.
 */

import { format } from 'date-fns';
import { FirestoreDateLike, toDateSafe } from '@utils/Date';

/** Cómo se nombra y cómo se lee un campo dentro de la frase. */
export interface FieldSpec {
    /** El campo, tal como se lee en la frase: `'el precio'`, `'las sucursales'`. */
    label: string;
    /** Cómo se muestra un valor. Por defecto, el valor tal cual. */
    format?: (value: unknown) => string;
    /**
     * Solo para banderas. Con esto, `status: true → false` se describe como un
     * acto (*"desactivó el producto"*) y no como un cambio de valor
     * (*"cambió el estado de sí a no"*), que es como lo diría una máquina.
     */
    verbs?: { on: string; off: string };
    /**
     * Frase fija para cuando el VALOR no se puede leer en voz alta: un id
     * (*"cambió la categoría de abc123 a xyz789"* no le dice nada a nadie) o un
     * arreglo de objetos (que saldría como `[object Object]`). Se registra que
     * cambió y qué cambió, sin pretender describir el contenido.
     */
    mention?: string;
}

export type FieldSpecs<T> = Partial<Record<keyof T & string, FieldSpec>>;

export interface Change {
    field: string;
    before: unknown;
    after: unknown;
    /** La frase suelta, ya formateada. */
    phrase: string;
}

/** `$189.00`. Para precios, costos, montos y tarifas. */
export const money = (value: unknown): string => `$${(Number(value) || 0).toFixed(2)}`;

/** `"Hamburguesa BBQ"`. Entrecomilla el texto para que se vea dónde empieza y termina. */
export const quoted = (value: unknown): string => {
    const text = value === undefined || value === null ? '' : String(value).trim();
    return text ? `"${text}"` : 'vacío';
};

/** `12%`. Para comisiones y porcentajes. */
export const percent = (value: unknown): string => `${Number(value) || 0}%`;

/** `12-03-2026 3:45 p. m.`. Para fechas de Firestore en cualquiera de sus formas. */
export const dateTime = (value: unknown): string => {
    const date = toDateSafe(value as FirestoreDateLike);
    return date ? format(date, 'dd-MM-yyyy p') : 'sin fecha';
};

/**
 * ¿Es un instante? **Solo objetos**: `Date` y las dos formas del Timestamp de
 * Firestore. Un número o un string NO cuentan — `new Date(189)` es una fecha
 * válida de 1970 y convertiría cualquier precio en una fecha.
 */
const esInstante = (value: unknown): boolean =>
    value instanceof Date ||
    (typeof value === 'object' && value !== null && ('toDate' in value || 'seconds' in value));

/**
 * ¿Es el MISMO valor? Normaliza lo que Firestore devuelve de formas distintas
 * según quién escribió el documento.
 *
 * ⚠️ **`0` y `false` son valores, no ausencias.** Es la trampa que se pagó en la
 * difunta `parseFirebaseDate` (preguntaba `date.seconds && date.nanoseconds`, así
 * que un `nanoseconds: 0` daba `Invalid Date`): un precio de $0 es legítimo en
 * Krispis —hay cortesías y promociones— y una bandera apagada es un estado, no un
 * campo vacío.
 */
const isEmpty = (value: unknown): boolean =>
    value === undefined || value === null || (typeof value === 'string' && value.trim() === '');

/** Huella estable para comparar estructuras (arreglos de objetos, objetos anidados). */
const fingerprint = (value: unknown): string => {
    try {
        return JSON.stringify(value) ?? '';
    } catch {
        return String(value);
    }
};

const sameValue = (before: unknown, after: unknown): boolean => {
    if (typeof before === 'object' && before !== null && typeof after === 'object' && after !== null) {
        if (!esInstante(before) && !esInstante(after)) return fingerprint(before) === fingerprint(after);
    }
    if (isEmpty(before) && isEmpty(after)) return true;
    if (isEmpty(before) || isEmpty(after)) return false;
    if (esInstante(before) || esInstante(after)) {
        const antes = toDateSafe(before as FirestoreDateLike);
        const despues = toDateSafe(after as FirestoreDateLike);
        return antes?.getTime() === despues?.getTime();
    }
    if (typeof before === 'boolean' || typeof after === 'boolean') return Boolean(before) === Boolean(after);
    // Firestore puede devolver el mismo número como number o como string según
    // quién lo escribió: comparar por valor, no por tipo.
    const beforeNumber = Number(before);
    const afterNumber = Number(after);
    if (Number.isFinite(beforeNumber) && Number.isFinite(afterNumber) && `${before}`.trim() !== '' && `${after}`.trim() !== '') {
        return beforeNumber === afterNumber;
    }
    return String(before).trim() === String(after).trim();
};

const describeList = (
    label: string,
    before: unknown[],
    after: unknown[],
    format: (value: unknown) => string
): string => {
    const antes = (before || []).map((item) => String(item));
    const despues = (after || []).map((item) => String(item));
    const agregados = despues.filter((item) => !antes.includes(item));
    const quitados = antes.filter((item) => !despues.includes(item));
    if (agregados.length === 0 && quitados.length === 0) return '';
    const partes: string[] = [];
    if (agregados.length > 0) partes.push(`agregó ${agregados.map(format).join(', ')}`);
    if (quitados.length > 0) partes.push(`quitó ${quitados.map(format).join(', ')}`);
    return `cambió ${label}: ${partes.join(' y ')}`;
};

/**
 * Los cambios, uno por uno. Úsalo cuando el módulo necesite el detalle; para la
 * frase de la bitácora, `describeChanges`.
 *
 * `before` ausente = creación: devuelve `[]` a propósito. Describir 20 campos
 * "de vacío a algo" no es una descripción, es un volcado — y la creación se
 * resuelve con su propia frase (o, mejor, con el `createdAt`/`createdBy` del
 * documento: D8/D9 del plan).
 */
// `T extends object` y no `Record<string, unknown>`: las interfaces del proyecto
// (`Order`, `Dish`…) no declaran índice de cadena, así que no encajarían en un
// Record. El acceso dinámico se hace con un cast local, no relajando la API.
export const getChanges = <T extends object>(
    before: T | undefined | null,
    after: T,
    specs: FieldSpecs<T>
): Change[] => {
    if (!before || !after) return [];
    const antes = before as Record<string, unknown>;
    const despues = after as Record<string, unknown>;
    const changes: Change[] = [];
    (Object.keys(specs) as string[]).forEach((field) => {
        const spec = specs[field as keyof T & string];
        if (!spec) return;
        const valorAntes = antes[field];
        const valorDespues = despues[field];
        const format = spec.format || ((value: unknown) => String(value));

        if ((Array.isArray(valorAntes) || Array.isArray(valorDespues)) && !spec.mention) {
            const phrase = describeList(
                spec.label,
                (valorAntes as unknown[]) || [],
                (valorDespues as unknown[]) || [],
                format
            );
            if (phrase) changes.push({ field, before: valorAntes, after: valorDespues, phrase });
            return;
        }
        if (sameValue(valorAntes, valorDespues)) return;

        // Frase fija: el valor no se puede leer en voz alta (un id, un arreglo).
        if (spec.mention) {
            changes.push({ field, before: valorAntes, after: valorDespues, phrase: spec.mention });
            return;
        }

        // Bandera con verbos: es un acto, no un cambio de valor.
        if (spec.verbs && (typeof valorDespues === 'boolean' || typeof valorAntes === 'boolean')) {
            changes.push({
                field,
                before: valorAntes,
                after: valorDespues,
                phrase: valorDespues ? spec.verbs.on : spec.verbs.off
            });
            return;
        }
        // Un campo que aparece o desaparece no se lee como "de vacío a X".
        if (isEmpty(valorAntes)) {
            changes.push({ field, before: valorAntes, after: valorDespues, phrase: `puso ${spec.label} en ${format(valorDespues)}` });
            return;
        }
        if (isEmpty(valorDespues)) {
            changes.push({ field, before: valorAntes, after: valorDespues, phrase: `quitó ${spec.label}` });
            return;
        }
        changes.push({
            field,
            before: valorAntes,
            after: valorDespues,
            phrase: `cambió ${spec.label} de ${format(valorAntes)} a ${format(valorDespues)}`
        });
    });
    return changes;
};

/** Une frases como se unen en español: `a`, `a y b`, `a, b y c`. */
export const joinPhrases = (phrases: string[]): string => {
    if (phrases.length === 0) return '';
    if (phrases.length === 1) return phrases[0];
    return `${phrases.slice(0, -1).join(', ')} y ${phrases[phrases.length - 1]}`;
};

/**
 * La frase completa para `addActivityLog`. `''` = no cambió nada = **no escribas
 * el log**.
 *
 * No trunca aunque cambien quince campos: la descripción es un string y el
 * espacio es barato, mientras que recortar pierde justo lo que alguien vendría a
 * auditar. Si un renglón queda largo, lo recorta la pantalla, no el dato.
 */
export const describeChanges = <T extends object>(
    before: T | undefined | null,
    after: T,
    specs: FieldSpecs<T>
): string => joinPhrases(getChanges(before, after, specs).map((change) => change.phrase));
